/**
 * Main Application Layout with Horizontal Top Navigation
 *
 * Responsive design:
 * - Desktop: Full horizontal navigation with search, tenant selector, notifications
 * - Tablet: Collapsed menu items, smaller search
 * - Mobile: Hamburger menu with dropdown navigation
 */

import { useState, useEffect } from 'react';
import { Outlet, useNavigate, useLocation } from 'react-router-dom';
import { Layout, Menu, Avatar, Dropdown, Typography, theme, Space, Button, Drawer, message } from 'antd';
import {
  DashboardOutlined,
  ProjectOutlined,
  FileTextOutlined,
  SettingOutlined,
  UserOutlined,
  LogoutOutlined,
  CalendarOutlined,
  MenuOutlined,
  FolderOutlined,
  InsertRowBelowOutlined,
} from '@ant-design/icons';
import { useAuthStore } from '@store/authStore';
import { useTerminology } from '@hooks/useTerminology';
import { NotificationCenter } from '@components/notifications';
import { OmniSearch } from '@components/search';
import TenantSelector from '@components/TenantSelector';
import VersionBanner from '@components/VersionBanner';
import { BookmarksBar } from '@components/bookmarks';
import KeyboardShortcutsHelp from '@components/KeyboardShortcutsHelp';
import './MainLayout.css';

const { Header, Content } = Layout;
const { Text } = Typography;

export default function MainLayout() {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, hasPermission } = useAuthStore();
  const { t } = useTerminology();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [shortcutsHelpOpen, setShortcutsHelpOpen] = useState(false);
  const {
    token: { colorBgContainer, colorPrimary, colorBgLayout, colorBorderSecondary },
  } = theme.useToken();

  // Global keyboard shortcuts
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      const target = e.target as HTMLElement;
      const isEditable =
        target.tagName === 'INPUT' ||
        target.tagName === 'TEXTAREA' ||
        target.isContentEditable;

      if (e.key === '?' && e.shiftKey && !isEditable) {
        e.preventDefault();
        setShortcutsHelpOpen(true);
        return;
      }

      if ((e.metaKey || e.ctrlKey) && e.key === 'n') {
        e.preventDefault();
        if (location.pathname === '/tasks' || location.pathname.startsWith('/tasks')) {
          navigate('/tasks?action=create');
          message.info('Press Cmd/Ctrl + N again or click New Task');
        } else if (location.pathname === '/templates' || location.pathname.startsWith('/templates')) {
          navigate('/templates?action=create');
          message.info('Press Cmd/Ctrl + N again or click New');
        } else {
          navigate('/tasks?action=create');
        }
        return;
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [location.pathname, navigate]);

  const navItems = [
    {
      key: '/',
      icon: <DashboardOutlined />,
      label: 'Dashboard',
    },
    {
      key: '/tasks',
      icon: <ProjectOutlined />,
      label: t('workItemNamePlural', 'Work Items'),
    },
    {
      key: '/templates',
      icon: <FileTextOutlined />,
      label: t('workPackageNamePlural', 'Work Packages'),
      show: hasPermission('templates:read'),
    },
    {
      key: '/calendar',
      icon: <CalendarOutlined />,
      label: 'Calendar',
    },
    {
      key: '/kanban',
      icon: <InsertRowBelowOutlined />,
      label: 'Kanban',
    },
    {
      key: '/files',
      icon: <FolderOutlined />,
      label: 'Files',
      show: hasPermission('storage:read'),
    },
  ].filter((item) => item.show !== false);

  const userMenuItems = [
    {
      key: 'profile',
      icon: <UserOutlined />,
      label: 'My Profile',
      onClick: () => navigate('/profile'),
    },
    {
      key: 'settings',
      icon: <SettingOutlined />,
      label: 'Settings',
      onClick: () => navigate('/settings'),
    },
    {
      type: 'divider' as const,
    },
    {
      key: 'logout',
      icon: <LogoutOutlined />,
      label: 'Cloudflare Logout',
      onClick: () => {
        window.location.href = '/cdn-cgi/access/logout';
      },
    },
  ];

  const handleNavClick = ({ key }: { key: string }) => {
    if (key.startsWith('/')) {
      navigate(key);
    }
  };

  const getSelectedKeys = () => {
    const path = location.pathname;
    if (path === '/') return ['/'];
    const match = navItems.find(
      (item) => path === item.key || (item.key !== '/' && path.startsWith(item.key))
    );
    return match ? [match.key] : [];
  };

  const handleMobileNavClick = ({ key }: { key: string }) => {
    handleNavClick({ key });
    setMobileMenuOpen(false);
  };

  return (
    <Layout style={{ minHeight: '100vh' }}>
      <Header
        style={{
          padding: '0 16px',
          background: colorBgContainer,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          borderBottom: `1px solid ${colorBorderSecondary}`,
          zIndex: 100,
          position: 'sticky',
          top: 0,
          height: 48,
          lineHeight: '48px',
        }}
      >
        {/* Left: Mobile Menu Button + Logo + Navigation */}
        <div style={{ display: 'flex', alignItems: 'center', flex: 1, minWidth: 0 }}>
          <Button
            type="text"
            icon={<MenuOutlined />}
            onClick={() => setMobileMenuOpen(true)}
            className="mobile-menu-btn"
            style={{ marginRight: 8 }}
          />

          <Text
            strong
            style={{
              fontSize: 15,
              marginRight: 20,
              cursor: 'pointer',
              whiteSpace: 'nowrap',
              flexShrink: 0,
              letterSpacing: '-0.3px',
            }}
            onClick={() => navigate('/')}
          >
            BizOps
          </Text>

          <Menu
            mode="horizontal"
            selectedKeys={getSelectedKeys()}
            items={navItems}
            onClick={handleNavClick}
            className="main-layout-nav desktop-menu"
            style={{
              border: 'none',
              background: 'transparent',
              flex: 1,
              minWidth: 0,
            }}
          />
        </div>

        {/* Right: Search, Tenant, Notifications, User */}
        <Space size={10} style={{ marginLeft: 8, flexShrink: 0 }}>
          <OmniSearch />
          <TenantSelector />
          <NotificationCenter />
          <Dropdown menu={{ items: userMenuItems }} placement="bottomRight" arrow>
            <Space style={{ cursor: 'pointer' }}>
              <Avatar
                size="small"
                style={{ backgroundColor: colorPrimary }}
                icon={<UserOutlined />}
              />
              {user && <Text className="desktop-user-name" style={{ fontSize: 13 }}>{user.fullName}</Text>}
            </Space>
          </Dropdown>
        </Space>
      </Header>

      {/* Mobile Navigation Drawer */}
      <Drawer
        title="Navigation"
        placement="left"
        onClose={() => setMobileMenuOpen(false)}
        open={mobileMenuOpen}
        width={280}
        styles={{ body: { padding: 0 } }}
      >
        <Menu
          mode="vertical"
          selectedKeys={getSelectedKeys()}
          items={navItems}
          onClick={handleMobileNavClick}
          style={{ border: 'none' }}
        />
        <div style={{ padding: '12px 24px', color: 'var(--text-tertiary)', fontSize: 11, fontWeight: 600, textTransform: 'uppercase' as const, letterSpacing: '0.5px' }}>
          Settings
        </div>
        <Menu
          mode="vertical"
          items={[
            { key: '/profile', icon: <UserOutlined />, label: 'My Profile' },
            { key: '/settings', icon: <SettingOutlined />, label: 'Settings' },
          ]}
          onClick={handleMobileNavClick}
          style={{ border: 'none' }}
        />
      </Drawer>

      {/* Bookmarks bar */}
      <BookmarksBar />

      <Content
        style={{
          padding: 16,
          background: colorBgLayout,
          minHeight: 'calc(100vh - 48px)',
          overflow: 'auto',
        }}
      >
        <Outlet />
      </Content>

      <VersionBanner />
      <KeyboardShortcutsHelp open={shortcutsHelpOpen} onClose={() => setShortcutsHelpOpen(false)} />
    </Layout>
  );
}
